/**
 * One `planWatchOrder` pass per (catalog, filters, pace, watched, today),
 * shared by every part of the plan view through context.
 *
 * Same reasoning as `lib/filters/context.tsx`: the summary, the timeline and
 * the cut list all read from one result, because a plan that says "11 stops"
 * above a timeline drawing twelve is worse than no plan at all.
 *
 * The solver is a pure function and its result carries a `deterministicHash`,
 * so memoising on its inputs is exact rather than approximate — the same
 * inputs cannot produce a different plan.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { type PlanResult, planWatchOrder } from "@/lib/solver/index.ts";
import { readNow } from "@/state/countdown.ts";
import { useFilters } from "@/state/filters.ts";
import { useProgressStore } from "@/state/progress.ts";
import { useSettingsStore } from "@/state/settings.ts";

const PlanContext = createContext<PlanResult | null>(null);

/**
 * Local calendar date, not UTC. The plan is measured in days-until, and a user
 * in UTC-5 planning at 8pm must not be told it is already tomorrow.
 *
 * Read from the countdown's clock rather than `Date.now()` directly: it is the
 * one clock in the app, so the plan and the countdown bar can never disagree
 * about what day it is, and `setNowForTests` pins both at once.
 */
function todayISO(): string {
  const now = new Date(readNow());
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function PlanProvider({ children }: { children: ReactNode }) {
  const catalog = useCatalog();
  const filters = useFilters();
  const watched = useProgressStore((s) => s.watched);
  const encoreMode = useSettingsStore((s) => s.encoreMode);
  const hoursPerWeek = useSettingsStore((s) => s.hoursPerWeek);

  // Deliberately a plain read rather than `useNow()`. Subscribing would
  // re-render the whole plan tree every second to move a value that changes
  // once a day; the sibling countdown bar already ticks, and any filter or
  // pace change re-renders this anyway.
  const today = todayISO();

  const plan = useMemo(
    () => planWatchOrder({ catalog, filters, today, hoursPerWeek, encoreMode, watched }),
    [catalog, filters, today, hoursPerWeek, encoreMode, watched],
  );

  return <PlanContext.Provider value={plan}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanResult {
  const plan = useContext(PlanContext);
  if (!plan) throw new Error("usePlan must be used inside PlanProvider");
  return plan;
}
